import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Refresh as RefreshIcon,
  TrendingUp as CreditIcon,
  TrendingDown as DebitIcon,
  Functions as NetIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import moment from 'moment-jalaali';
import { toast } from 'react-toastify';
import ProtectedRoute from '../components/common/ProtectedRoute';
import JalaliDateField from '../components/common/JalaliDateField';
import financeLedgerService, {
  PeriodicReport,
  LedgerSource,
  SOURCE_LABELS,
} from '../services/financeLedgerService';
import './EntityManagement.css';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

const formatToman = (n: number | null | undefined): string => {
  if (n == null || !isFinite(Number(n))) return '—';
  return `${new Intl.NumberFormat('fa-IR').format(Number(n))} تومان`;
};

const jalaliToISO = (value: string, endOfDay = false): string | undefined => {
  const v = value.trim();
  if (!v) return undefined;
  const m = moment(v, 'jYYYY/jMM/jDD');
  if (!m.isValid()) return undefined;
  if (endOfDay) m.endOf('day');
  else m.startOf('day');
  return m.toISOString();
};

const SOURCE_OPTIONS = Object.entries(SOURCE_LABELS) as Array<[LedgerSource, string]>;

const FinanceReports: React.FC = () => {
  const [period, setPeriod] = useState<'daily' | 'monthly'>('daily');
  const [fromJalali, setFromJalali] = useState(moment().subtract(30, 'days').format('jYYYY/jMM/jDD'));
  const [toJalali, setToJalali] = useState(moment().format('jYYYY/jMM/jDD'));
  const [source, setSource] = useState<'' | LedgerSource>('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<PeriodicReport | null>(null);

  const fetchReport = useCallback(async (p: 'daily' | 'monthly', fromJ: string, toJ: string, src: '' | LedgerSource) => {
    setLoading(true);
    try {
      const data = await financeLedgerService.getPeriodicReport({
        from: jalaliToISO(fromJ),
        to: jalaliToISO(toJ, true),
        period: p,
        sources: src ? [src] : undefined,
      });
      setReport(data);
    } catch {
      toast.error('خطا در دریافت گزارش');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(period, fromJalali, toJalali, source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePeriodChange = (p: 'daily' | 'monthly') => {
    setPeriod(p);
    // monthly defaults to a 12-month window for a useful view
    if (p === 'monthly') {
      const from = moment().subtract(12, 'jMonth' as any).format('jYYYY/jMM/jDD');
      setFromJalali(from);
      fetchReport(p, from, toJalali, source);
    } else {
      fetchReport(p, fromJalali, toJalali, source);
    }
  };

  const maxAbs = useMemo(() => {
    if (!report) return 1;
    return Math.max(1, ...report.rows.map((r) => Math.max(r.moneyIn || 0, r.moneyOut || 0)));
  }, [report]);

  const sourcesInReport = useMemo(() => {
    if (!report) return [] as string[];
    const set = new Set<string>();
    report.rows.forEach((r) => Object.keys(r.bySource).forEach((s) => set.add(s)));
    return Array.from(set);
  }, [report]);

  // ✅ FIX (2026-07-03): کارت‌ها قبلاً جمع «همه دفاتر» را نشان می‌دادند — یک فروش
  // ۱٫۱ میلیونی هم به‌عنوان پرداخت درگاه و هم به‌عنوان بستانکاری فروشنده شمرده و
  // ۲٫۲ میلیون نمایش داده می‌شد. حالا فقط پول واقعی که وارد/خارج شده نشان داده می‌شود.
  const totalsCards = [
    { title: 'ورود واقعی پول (درگاه)', value: report?.totals.moneyIn, icon: <CreditIcon />, color: 'green' },
    { title: 'خروج واقعی پول (استرداد + برداشت‌ها)', value: report?.totals.moneyOut, icon: <DebitIcon />, color: 'red' },
    { title: 'خالص جریان نقدی دوره', value: report?.totals.moneyNet, icon: <NetIcon />, color: 'blue' },
  ];

  return (
    <ProtectedRoute requiredPermissions={['admin_finance_reports']}>
      <Helmet>
        <title>گزارش‌های دوره‌ای مالی</title>
      </Helmet>

      <div className="entity-page" dir="rtl">
        <div className="entity-header">
          <div>
            <h1 className="entity-title">
              <ReportIcon style={{ verticalAlign: 'middle', marginLeft: 8 }} />
              گزارش‌های دوره‌ای مالی
            </h1>
            <p className="entity-subtitle">جریان واقعی پول به تفکیک روز یا ماه شمسی — «ورود/خروج پول» فقط پول واقعی است؛ ستون‌های «گردش دفاتر» ثبت‌های موازی (سهم فروشنده، کیف‌پول و…) را هم می‌شمارند</p>
          </div>
          <div className="header-actions">
            <button
              className="header-btn refresh"
              onClick={() => fetchReport(period, fromJalali, toJalali, source)}
              disabled={loading}
              title="بروزرسانی"
            >
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="edit-card" style={{ marginBottom: 16 }}>
          <div className="edit-card-body">
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={period}
                onChange={(_e, v) => v && handlePeriodChange(v)}
              >
                <ToggleButton value="daily">روزانه</ToggleButton>
                <ToggleButton value="monthly">ماهانه (شمسی)</ToggleButton>
              </ToggleButtonGroup>
              <JalaliDateField label="از تاریخ (شمسی)" value={fromJalali} onChange={setFromJalali} />
              <JalaliDateField label="تا تاریخ (شمسی)" value={toJalali} onChange={setToJalali} />
              <TextField
                select
                size="small"
                label="منبع"
                value={source}
                onChange={(e) => setSource(e.target.value as any)}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">همه منابع</MenuItem>
                {SOURCE_OPTIONS.map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={() => fetchReport(period, fromJalali, toJalali, source)}
                disabled={loading}
              >
                نمایش گزارش
              </Button>
            </Box>
          </div>
        </div>

        {/* Totals */}
        <div className="entity-stats">
          {totalsCards.map((c, i) => (
            <div key={i} className="stat-card">
              <div className={`stat-icon ${c.color}`}>{c.icon}</div>
              <div className="stat-content">
                <div className="stat-label">{c.title}</div>
                <div className="stat-value">{c.value == null ? '—' : formatToman(c.value)}</div>
              </div>
            </div>
          ))}
        </div>

        {loading && !report ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : report && report.rows.length === 0 ? (
          <div className="edit-card">
            <div className="edit-card-body">
              <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                در این بازه گردش مالی ثبت نشده است
              </p>
            </div>
          </div>
        ) : report ? (
          <div className="edit-card">
            <div className="edit-card-header">
              {period === 'daily' ? 'گردش روزانه' : 'گردش ماهانه (ماه شمسی)'}
              {' — '}
              {toPersianDigits(report.rows.length)} {period === 'daily' ? 'روز' : 'ماه'}
            </div>
            <div className="edit-card-body" style={{ overflowX: 'auto' }}>
              <table className="entity-table">
                <thead>
                  <tr>
                    <th>{period === 'daily' ? 'تاریخ' : 'ماه'}</th>
                    <th>نمودار</th>
                    <th title="پرداخت‌های موفق درگاه — پول واقعی که وارد پلتفرم شد">ورود پول</th>
                    <th title="استرداد درگاه + برداشت‌های تکمیل‌شده — پول واقعی که خارج شد">خروج پول</th>
                    <th>خالص نقدی</th>
                    <th title="جمع بستانکاری همه دفاتر — یک پول ممکن است در چند دفتر ثبت موازی داشته باشد (مثلاً پرداخت درگاه + بستانکاری فروشنده)">گردش بستانکار دفاتر</th>
                    <th title="جمع بدهکاری همه دفاتر — شامل ثبت‌های موازی">گردش بدهکار دفاتر</th>
                    <th>تعداد سند</th>
                    {sourcesInReport.map((s) => (
                      <th key={s}>{SOURCE_LABELS[s as LedgerSource] || s} (خالص)</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...report.rows].reverse().map((r) => (
                    <tr key={r.period}>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{toPersianDigits(r.period)}</td>
                      <td style={{ minWidth: 140 }}>
                        {/* tiny CSS bar chart: real money in vs out */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{
                            height: 6,
                            width: `${Math.max(2, ((r.moneyIn || 0) / maxAbs) * 100)}%`,
                            background: 'var(--color-success, #2e7d32)',
                            borderRadius: 3,
                          }} />
                          <div style={{
                            height: 6,
                            width: `${Math.max(2, ((r.moneyOut || 0) / maxAbs) * 100)}%`,
                            background: 'var(--color-error, #c62828)',
                            borderRadius: 3,
                          }} />
                        </div>
                      </td>
                      <td style={{ color: 'var(--color-success-dark)', whiteSpace: 'nowrap', fontWeight: 600 }}>{formatToman(r.moneyIn)}</td>
                      <td style={{ color: 'var(--color-error-dark)', whiteSpace: 'nowrap', fontWeight: 600 }}>{formatToman(r.moneyOut)}</td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatToman(r.moneyNet)}</td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>{formatToman(r.credit)}</td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>{formatToman(r.debit)}</td>
                      <td>{new Intl.NumberFormat('fa-IR').format(r.count)}</td>
                      {sourcesInReport.map((s) => {
                        const v = r.bySource[s];
                        const net = v ? v.credit - v.debit : 0;
                        return (
                          <td key={s} style={{ whiteSpace: 'nowrap', color: net < 0 ? 'var(--color-error-dark)' : undefined }}>
                            {v ? formatToman(net) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </ProtectedRoute>
  );
};

export default FinanceReports;
